import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchPractice, fetchRehearsal } from '../firebase/fetcher';
import { TestQuizEntity } from '../types';
import { useProgress } from '../contexts/ProgressContext';
import QuestionView from '../components/QuestionView';
import ChoiceList from '../components/ChoiceList';
import ExplanationModal from '../components/ExplanationModal';
import Footer from '../components/Footer';
import AdBanner from '../components/AdBanner';

interface QuizState {
  selected: number | null;
  showModal: boolean;
}

interface Props {
  mode?: 'practice' | 'rehearsal';
}

export default function PracticeTest({ mode = 'practice' }: Props) {
  const { testIdentifier } = useParams<{ testIdentifier: string }>();
  const navigate = useNavigate();
  const { markCompleted } = useProgress();

  const [quizzes, setQuizzes] = useState<TestQuizEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [states, setStates] = useState<QuizState[]>([]);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!testIdentifier) return;
    const fetch = mode === 'rehearsal' ? fetchRehearsal : fetchPractice;
    fetch(testIdentifier)
      .then(qs => {
        setQuizzes(qs);
        setStates(qs.map(() => ({ selected: null, showModal: false })));
      })
      .finally(() => setLoading(false));
  }, [testIdentifier, mode]);

  function select(index: number) {
    setStates(prev => prev.map((s, i) => i === current ? { ...s, selected: index } : s));
  }

  function toggleModal(show: boolean) {
    setStates(prev => prev.map((s, i) => i === current ? { ...s, showModal: show } : s));
  }

  function next() {
    if (current < quizzes.length - 1) {
      setCurrent(c => c + 1);
    } else {
      const key = testIdentifier!;
      markCompleted(key);
      setFinished(true);
    }
  }

  if (loading) return <div className="page loading">読み込み中...</div>;
  if (quizzes.length === 0) return <div className="page loading">問題が見つかりません</div>;

  if (finished) {
    const score = states.reduce((acc, s, i) => acc + (s.selected === quizzes[i].answer ? quizzes[i].allocation : 0), 0);
    const total = quizzes.reduce((acc, q) => acc + q.allocation, 0);
    return (
      <div className="page result-page">
        <header className="nav-header">
          <h1 className="nav-title">結果</h1>
        </header>
        <div className="result-content">
          <div className="result-score">{score} <span className="result-total">/ {total} 点</span></div>
          <div className="result-label">{score === total ? '満点！' : score >= total * 0.8 ? 'よくできました' : 'もう一度チャレンジしよう'}</div>
          <button className="primary-btn" onClick={() => navigate(-2)}>章に戻る</button>
        </div>
        <AdBanner />
      </div>
    );
  }

  const quiz = quizzes[current];
  const state = states[current];
  const result = state.selected === null ? null : state.selected === quiz.answer ? 'correct' : 'wrong';

  return (
    <div className="page lesson-page">
      <header className="nav-header">
        <button className="back-btn" onClick={() => navigate(-1)}>✕</button>
        <h1 className="nav-title">{mode === 'rehearsal' ? 'テスト予想問題' : 'チェックテスト'}</h1>
        <span className="progress-indicator">{current + 1} / {quizzes.length}</span>
      </header>
      <div className="lesson-content">
        <QuestionView
          quiz={quiz.quiz}
          theme={quiz.theme}
          empha={quiz.empha}
          translate={quiz.translate}
          result={result as any}
        />
        <ChoiceList
          choices={quiz.choice}
          correctIndex={quiz.answer}
          selectedIndex={state.selected}
          onSelect={select}
        />
      </div>
      <Footer
        onComment={state.selected !== null ? () => toggleModal(true) : undefined}
        onNext={state.selected !== null ? next : undefined}
        nextLabel={current === quizzes.length - 1 ? '結果へ' : '次へ'}
      />
      {state.showModal && (
        <ExplanationModal comment={quiz.comment} onClose={() => toggleModal(false)} />
      )}
    </div>
  );
}
